import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import React from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/context/AuthContext'
import { useTranslation } from '@/hooks/useTranslation'

const getFormSchema = (t: (key: string) => string) => z.object({
	name: z.string().min(2, t('accountSettings.nameMinLength')),
	email: z.string().email(t('accountSettings.invalidEmail')),
	company: z.string().optional(),
})

const AccountSettings = () => {
	const { t } = useTranslation()
	const { toast } = useToast()

	// Use auth context instead of making redundant getUser() calls
	const { user: authUser, loading: authLoading } = useAuth()
	const formSchema = React.useMemo(() => getFormSchema(t), [t])

	// Transform auth user data for the form
	const user = React.useMemo(() => {
		if (!authUser) return null

		return {
			name: authUser.user_metadata?.full_name || '',
			email: authUser.email || '',
			company: authUser.user_metadata?.company || '',
		}
	}, [authUser])

	const isLoading = authLoading || !authUser
	const isError = false
	const error = null

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: user?.name || '',
			email: user?.email || '',
			company: user?.company || '',
		},
		values: user
			? {
					name: user.name || '',
					email: user.email || '',
					company: user.company || '',
			  }
			: undefined,
	})

	// Reset form when user data changes
	React.useEffect(() => {
		if (user) {
			form.reset({
				name: user.name || '',
				email: user.email || '',
				company: user.company || '',
			})
		}
	}, [user, form, isLoading, authUser])

	const onSubmit = async (values: z.infer<typeof formSchema>) => {
		try {
			const { error } = await supabase.auth.updateUser({
				data: {
					full_name: values.name,
					company: values.company,
				},
			})

			if (error) {
				throw error
			}

			toast({
				title: t('accountSettings.settingsUpdated'),
				description: t('accountSettings.settingsUpdatedDescription'),
			})
		} catch (error: any) {
			toast({
				variant: 'destructive',
				title: t('accountSettings.error'),
				description: t('accountSettings.failedToSave'),
			})
		}
	}

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="space-y-2">
					<Skeleton className="h-4 w-12" /> {/* Label */}
					<Skeleton className="h-10 w-full" /> {/* Input */}
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-12" /> {/* Label */}
					<Skeleton className="h-10 w-full" /> {/* Input */}
					<Skeleton className="h-4 w-64" /> {/* Description */}
				</div>
				<div className="space-y-2">
					<Skeleton className="h-4 w-12" /> {/* Label */}
					<Skeleton className="h-10 w-full" /> {/* Input */}
				</div>
				<Skeleton className="h-10 w-28" /> {/* Button */}
			</div>
		)
	}

	if (!authUser && !authLoading) {
		return (
			<div className="text-red-500">
				{t('accountSettings.pleaseSignIn')}
			</div>
		)
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('accountSettings.name')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('accountSettings.email')}</FormLabel>
							<FormControl>
								<Input {...field} disabled />
							</FormControl>
							<FormDescription>
								{t('accountSettings.emailCannotBeChanged')}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<Button type="submit">{t('accountSettings.saveChanges')}</Button>
			</form>
		</Form>
	)
}

export default AccountSettings
